import { Component, Output } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { CanDisable, mixinDisabled } from '@angular/material/core';
import {
  Countertop,
  CustomValidators,
  CuttingMaterial,
  CuttingPanelListService,
  DirectivesModule,
  PanelSettings,
} from 'cloud-shared-lib';
import { Observable, debounceTime, map, tap } from 'rxjs';
import { DisableInputSpinEventsDirective } from '../../../directives/disable-input-spin-events.directive';

import { AsyncPipe, NgIf } from '@angular/common';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { CountertopSettingsStateService } from '../countertop-settings-state.service';

export class SizesBase {}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const _SizesMixinBase = mixinDisabled(SizesBase);

@Component({
  selector: 'csl-sizes',
  templateUrl: './sizes.component.html',
  styleUrls: ['./sizes.component.scss'],
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['disabled'],
  standalone: true,
  imports: [
    NgIf,
    FormsModule,
    FlexModule,
    DisableInputSpinEventsDirective,
    ReactiveFormsModule,
    MatLegacyFormFieldModule,
    MatLegacyInputModule,
    AsyncPipe,
    MatIconModule,
    DirectivesModule,
  ],
})
export class SizesComponent extends _SizesMixinBase implements CanDisable {
  countertop: Countertop;

  data$: Observable<any>;
  formChanged$: Observable<any>;

  material: CuttingMaterial;

  form: UntypedFormGroup = this.fb.group({
    length: [0, [Validators.required, Validators.min(0), CustomValidators.notEqual(0)]],
    width: [0, [Validators.required, Validators.min(0), CustomValidators.notEqual(0)]],
  });

  @Output() valid = this.form.statusChanges.pipe(map(s => s === 'VALID'));

  constructor(
    private cuttingPanelListService: CuttingPanelListService,
    private countertopSettingsService: CountertopSettingsStateService,
    private fb: UntypedFormBuilder
  ) {
    super();

    this.data$ = this.countertopSettingsService.countertop$.pipe(
      tap(p => {
        this.countertop = p;

        this.form.controls.width.setValue(this.countertop.width);
        this.form.controls.length.setValue(this.countertop.length);
        this.updateTreeValidity(this.form);

        this.material = this.countertop?.material;

        if (this.disabled) {
          this.form.disable();
        } else {
          this.form.enable();

          this.form.controls.length.addValidators(this.lengthValidator());
          this.form.controls.width.addValidators(this.widthValidator());

          this.updateTreeValidity(this.form);
        }
      })
    );

    this.formChanged$ = this.form.valueChanges.pipe(
      debounceTime(600),
      tap(() => {
        try {
          if (
            (this.countertop.length !== this.form.controls.length.value ||
              this.countertop.width !== this.form.controls.width.value) &&
            this.form.valid
          ) {
            this.countertop.length = this.form.controls.length.value;
            this.countertop.width = this.form.controls.width.value;

            this.countertopSettingsService.updateCountertop(this.countertop);
          }
        } catch (error) {
          if (error instanceof Error) {
            console.log(error);
            this.form.setErrors({ ...this.form.errors, size: error.message });
          }
        }
      })
    );
  }

  get panelsSettings(): PanelSettings {
    return this.cuttingPanelListService.onCuttingOptionsChanged.value.panels;
  }

  widthValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const valid = this.countertop.canSetWidth(control.value);

      return valid ? null : { invalidWidth: { value: control.value } };
    };
  }

  lengthValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const valid = this.countertop.canSetLength(control.value);

      return valid ? null : { invalidLength: { value: control.value } };
    };
  }

  private updateTreeValidity(
    group: UntypedFormGroup | UntypedFormArray,
    opts?: {
      onlySelf?: boolean;
      emitEvent?: boolean;
    }
  ): void {
    Object.keys(group.controls).forEach((key: string) => {
      const abstractControl = group.controls[key];

      if (abstractControl instanceof UntypedFormGroup || abstractControl instanceof UntypedFormArray) {
        this.updateTreeValidity(abstractControl, opts);
      } else {
        abstractControl.updateValueAndValidity(opts);
      }
    });
  }
}
